import { defaultLocale, isLocale, type Locale, locales } from "./locales.ts";

export const LOCALE_COOKIE = "vaxy_locale";

const BOT_UA =
  /Googlebot|bingbot|GPTBot|ClaudeBot|PerplexityBot|Applebot|DuckDuckBot|YandexBot|Baiduspider|Bytespider|CCBot|anthropic|Claude-Web|meta-externalagent|Google-Extended|ChatGPT|Slurp|ia_archiver/i;

export const TIMEZONE_LOCALE: Record<string, Locale> = {
  "Europe/Amsterdam": "nl",
  "Europe/Brussels": "nl",
  "Europe/Berlin": "de",
  "Europe/Vienna": "de",
  "Europe/Zurich": "de",
  "Europe/Paris": "fr",
  "Europe/Madrid": "es",
  "Atlantic/Canary": "es",
};

export function isCrawler(userAgent: string | undefined): boolean {
  return Boolean(userAgent && BOT_UA.test(userAgent));
}

export function languageLocales(languages: readonly string[]): Locale[] {
  const found: Locale[] = [];
  for (const language of languages) {
    const lower = language.toLowerCase();
    if (isLocale(lower)) {
      found.push(lower);
      continue;
    }
    const base = lower.split("-")[0];
    if (base && isLocale(base)) found.push(base);
  }
  return found;
}

export function pickLocale(opts: {
  cookie?: string | null;
  languages?: readonly string[];
  timeZone?: string | null;
}): Locale {
  const cookie = opts.cookie ?? null;
  if (cookie && isLocale(cookie)) return cookie;

  const found = languageLocales(opts.languages ?? []);
  const regional =
    opts.timeZone && TIMEZONE_LOCALE[opts.timeZone]
      ? TIMEZONE_LOCALE[opts.timeZone]
      : null;

  if (regional && found.includes(regional)) return regional;
  if (found[0] && found[0] !== "en") return found[0];
  if (regional) return regional;
  if (found[0]) return found[0];
  return defaultLocale;
}

export function matchLocale(languages: readonly string[]): Locale {
  return pickLocale({ languages });
}

export function parseLocaleCookie(
  cookieHeader: string | undefined,
): Locale | null {
  if (!cookieHeader) return null;
  const match = cookieHeader.match(/(?:^|;\s*)vaxy_locale=([^;]*)/);
  const value = match?.[1] ? decodeURIComponent(match[1]) : "";
  return isLocale(value) ? value : null;
}

export function localeCookie(locale: Locale): string {
  return `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

// ponytail: client stand-in for Accept-Language. Ceiling: no edge CF-IPCountry.
// Upgrade: Cloudflare Worker on `/` reading Cookie + Accept-Language + CF-IPCountry.
export const detectLocaleScript = `(function(){
  var locales=${JSON.stringify(locales)};
  var tzMap=${JSON.stringify(TIMEZONE_LOCALE)};
  var ua=navigator.userAgent||"";
  if (${BOT_UA}.test(ua)) return;
  function cookie(n){
    var m=document.cookie.match("(?:^|; )"+n+"=([^;]*)");
    return m?decodeURIComponent(m[1]):"";
  }
  function isLoc(x){return locales.indexOf(x)>=0}
  var c=cookie("${LOCALE_COOKIE}");
  var pick=isLoc(c)?c:null;
  if(!pick){
    var langs=navigator.languages||[navigator.language];
    var found=[];
    for(var i=0;i<langs.length;i++){
      var l=String(langs[i]||"").toLowerCase();
      if(isLoc(l)) found.push(l);
      else {
        var base=l.split("-")[0];
        if(base&&isLoc(base)) found.push(base);
      }
    }
    var tz="";
    try{tz=Intl.DateTimeFormat().resolvedOptions().timeZone||""}catch(e){}
    var regional=tzMap[tz]||null;
    if(regional&&found.indexOf(regional)>=0) pick=regional;
    else if(found[0]&&found[0]!=="en") pick=found[0];
    else if(regional) pick=regional;
    else if(found[0]) pick=found[0];
  }
  pick=pick||"${defaultLocale}";
  var path=location.pathname;
  if(path==="/"||path===""||path==="/index.html"){
    location.replace("/"+pick+"/"+location.search+location.hash);
  }
})();`;
